import { ApiError, NetworkError } from '@/api/problem';
import { problemHeading, problemTone } from '@/api/problemDisplay';
import { Notice } from '@/components/PageHeading';

/**
 * A failed API call, shown *in place*.
 *
 * Most failures are events and belong in the corner — see `useNotify`, which is what nearly every
 * screen now calls. This remains for the one case a toast handles badly: a page that could not load
 * at all. There, a toast alone leaves an empty screen and then takes the only explanation with it
 * when it expires, so the content area has to say something that stays.
 *
 * The heading and the tone come from the same two functions the toast uses, so the two renderings
 * of one failure can never disagree about how serious it is.
 */
export function ApiErrorAlert({ error }: { error: unknown }) {
  if (error instanceof NetworkError) {
    return (
      <Notice tone="warning" title="Cannot reach the server">
        Check your connection and try again.
      </Notice>
    );
  }

  if (!(error instanceof ApiError)) {
    return (
      <Notice tone="danger" title="Something went wrong">
        {error instanceof Error ? error.message : String(error)}
      </Notice>
    );
  }

  return (
    <Notice tone={problemTone(error)} title={problemHeading(error)}>
      {error.message}
    </Notice>
  );
}
